/// Pre-departure safety checklist — the list every skipper runs
/// before slipping lines. Grouped by category so the player can tick
/// items off as they go round the boat. Items are intentionally
/// concise and verb-led; the optional `note` carries the "watch out
/// for…" detail.
///
/// The check state is persisted in localStorage by the apps/web layer,
/// not here — this package is pure data so it can be reused on iOS
/// + Android via the same `id` keys without coupling to a runtime
/// storage layer.

export type Category =
  | "safety-equipment"
  | "engine"
  | "electronics"
  | "navigation"
  | "boat"
  | "crew"
  | "paperwork";

export function categoryLabel(c: Category): string {
  switch (c) {
    case "safety-equipment":
      return "Safety equipment";
    case "engine":
      return "Engine";
    case "electronics":
      return "Electronics + comms";
    case "navigation":
      return "Navigation + weather";
    case "boat":
      return "Boat condition";
    case "crew":
      return "Crew";
    case "paperwork":
      return "Paperwork";
  }
}

export const CATEGORIES: readonly Category[] = [
  "safety-equipment",
  "engine",
  "electronics",
  "navigation",
  "boat",
  "crew",
  "paperwork",
];

export interface ChecklistItem {
  /// Stable identifier — used as the localStorage key and in tests.
  id: string;
  category: Category;
  /// Short verb-led action.
  text: string;
  /// Optional detail that expands on the item — "watch out for…",
  /// "RYA recommends…", regulatory notes.
  note?: string;
}

export const CHECKLIST: readonly ChecklistItem[] = [
  // ── Safety equipment ────────────────────────────────────────────
  {
    id: "lifejackets-count",
    category: "safety-equipment",
    text: "Lifejacket on board for every crew member, correctly sized.",
    note: "Inflatable lifejackets need their bobbins / hydrostatic capsules within service date and the cylinder weighed.",
  },
  {
    id: "lifejackets-worn",
    category: "safety-equipment",
    text: "Lifejackets worn (or available) per the skipper's policy for the conditions.",
  },
  {
    id: "harness-tether",
    category: "safety-equipment",
    text: "Harness + tether available for every crew member; jackstays rigged for offshore.",
  },
  {
    id: "lifering-mob",
    category: "safety-equipment",
    text: "Lifering with light + drogue ready to throw, danbuoy free in its socket.",
  },
  {
    id: "flares",
    category: "safety-equipment",
    text: "Flares in their grab-bag, within expiry, accessible from the cockpit.",
    note: "RYA + Coastguard recommend a full pyrotechnic pack offshore; inshore minimum is hand flares + smoke.",
  },
  {
    id: "fire-extinguishers",
    category: "safety-equipment",
    text: "Fire extinguishers + fire blanket in service, accessible from the galley + companionway.",
    note: "MGN 599 lists the UK pleasure-craft minimums. Check the pressure gauges / service stickers.",
  },
  {
    id: "first-aid",
    category: "safety-equipment",
    text: "First-aid kit checked, contents in date, location known to crew.",
  },
  {
    id: "liferaft",
    category: "safety-equipment",
    text: "Liferaft in service, painter secured to a strong point, hydrostatic release armed.",
    note: "Liferafts have an annual or biennial service requirement. Don't lash it down — hydrostatic release won't activate if it can't float free.",
  },
  {
    id: "epirb-plb",
    category: "safety-equipment",
    text: "EPIRB or PLB registered (UK: MCA), battery in date, mounting/strap in good condition.",
  },
  {
    id: "grab-bag",
    category: "safety-equipment",
    text: "Grab bag packed and accessible — flares, water, handheld VHF, torch, knife, first-aid.",
  },

  // ── Engine ──────────────────────────────────────────────────────
  {
    id: "engine-oil",
    category: "engine",
    text: "Engine oil level on the dipstick — within marks.",
  },
  {
    id: "engine-coolant",
    category: "engine",
    text: "Coolant level in the header tank — within marks. Cap cold before opening.",
  },
  {
    id: "engine-belt",
    category: "engine",
    text: "Alternator + water-pump belts taut + no fraying.",
  },
  {
    id: "raw-water-strainer",
    category: "engine",
    text: "Raw-water strainer clean; sea-cock open.",
    note: "First common reason for an overheat alarm on the way out of the marina.",
  },
  {
    id: "fuel-level",
    category: "engine",
    text: "Fuel level adequate for the planned passage + 30 % reserve.",
  },
  {
    id: "fuel-leaks",
    category: "engine",
    text: "Fuel lines visually clear, no leaks at the filter or injectors.",
  },
  {
    id: "engine-start",
    category: "engine",
    text: "Engine started + run for ~5 minutes — water flowing out of exhaust, no alarms, gauges normal.",
  },

  // ── Electronics + comms ─────────────────────────────────────────
  {
    id: "battery-state",
    category: "electronics",
    text: "Domestic + engine batteries above 12.5 V at rest; charge under engine start.",
  },
  {
    id: "vhf-test",
    category: "electronics",
    text: "Fixed VHF radio test — DSC test call to a partner station, voice check.",
    note: "DO NOT test DSC by pressing the red distress button. Use 'Test Call' from the menu.",
  },
  {
    id: "vhf-handheld",
    category: "electronics",
    text: "Handheld VHF charged + in the grab-bag.",
  },
  {
    id: "nav-lights",
    category: "electronics",
    text: "Navigation lights — all switched on in turn, every bulb confirmed lit.",
    note: "Especially important if leaving in daylight expecting to return after dark.",
  },
  {
    id: "interior-lights",
    category: "electronics",
    text: "Cabin + chart-table red lights working.",
  },
  {
    id: "plotter-fix",
    category: "electronics",
    text: "Chartplotter on, fix acquired with HDOP < 2 (or fix-quality indicator solid).",
    note: "A bad fix at the pontoon often means a corroded antenna connector — don't sail with it.",
  },
  {
    id: "ais",
    category: "electronics",
    text: "AIS transmitting (if fitted) — own-ship icon visible on the plotter or a partner station.",
  },

  // ── Navigation + weather ────────────────────────────────────────
  {
    id: "weather-forecast",
    category: "navigation",
    text: "Latest synoptic + inshore forecast checked. Wind, visibility, sea state acceptable for crew + boat.",
    note: "UK: Met Office Inshore Waters + Shipping Forecast; or PredictWind / Windy as supplements, not replacements.",
  },
  {
    id: "tide-times",
    category: "navigation",
    text: "HW + LW times for departure + arrival ports noted.",
  },
  {
    id: "tidal-streams",
    category: "navigation",
    text: "Tidal streams for the passage worked out — favourable tide windows identified.",
  },
  {
    id: "passage-plan",
    category: "navigation",
    text: "Passage plan drawn — waypoints, COG, ETAs, tidal gates, bolt-holes.",
  },
  {
    id: "passage-plan-filed",
    category: "navigation",
    text: "Passage plan + ETA shared with a responsible person ashore.",
  },
  {
    id: "charts-paper",
    category: "navigation",
    text: "Paper chart of the passage area at the chart table + corrected up to date.",
  },
  {
    id: "almanac-pilot",
    category: "navigation",
    text: "Almanac + pilot book for the destination on board.",
  },

  // ── Boat condition ──────────────────────────────────────────────
  {
    id: "bilge",
    category: "boat",
    text: "Bilges empty + dry; bilge pump test (manual + electric).",
  },
  {
    id: "seacocks",
    category: "boat",
    text: "Sea-cocks — heads + sink set correctly for sea; engine raw-water open.",
  },
  {
    id: "rigging",
    category: "boat",
    text: "Rigging visually inspected — split pins seized, shrouds tensioned, no broken strands.",
  },
  {
    id: "sails",
    category: "boat",
    text: "Sails inspected — battens secured, no torn sailcloth, reefing lines run cleanly.",
  },
  {
    id: "anchor-ready",
    category: "boat",
    text: "Primary anchor + rode flaked, secured but ready to deploy.",
  },
  {
    id: "mooring-lines",
    category: "boat",
    text: "Mooring lines + fenders to hand for both departure and arrival.",
  },
  {
    id: "gas-isolated",
    category: "boat",
    text: "Gas locker valve closed unless cooking; stove tap off.",
    note: "Boat gas is heavier than air — a leak settles in the bilge. Smell test the locker before opening the valve.",
  },
  {
    id: "loose-gear",
    category: "boat",
    text: "Loose gear stowed — galley locker latches, sliding chart table secured.",
  },

  // ── Crew ────────────────────────────────────────────────────────
  {
    id: "crew-experience",
    category: "crew",
    text: "Crew experience appropriate to forecast conditions.",
  },
  {
    id: "safety-brief",
    category: "crew",
    text: "Safety briefing delivered — lifejacket use, MOB drill, fire response, abandon-ship signal, lifeerafter location.",
  },
  {
    id: "seasickness",
    category: "crew",
    text: "Seasickness preventives offered to crew who need them (Stugeron, etc.) before slipping.",
  },
  {
    id: "watch-rotation",
    category: "crew",
    text: "Watch rotation agreed for passages > 4 hours.",
  },
  {
    id: "head-count",
    category: "crew",
    text: "Number on board agreed — used in the Mayday script if needed.",
  },

  // ── Paperwork ───────────────────────────────────────────────────
  {
    id: "insurance",
    category: "paperwork",
    text: "Insurance certificate in date + on board (or accessible digitally).",
  },
  {
    id: "ssr-registration",
    category: "paperwork",
    text: "Vessel registration (UK: SSR / Part 1) certificate on board.",
  },
  {
    id: "vhf-licence",
    category: "paperwork",
    text: "Ship Radio Licence + skipper's SRC operator certificate available.",
  },
  {
    id: "passport-customs",
    category: "paperwork",
    text: "Passports + customs paperwork (Schengen, C1331, etc.) on board if crossing borders.",
  },
];

export const CHECKLIST_BY_CATEGORY: Readonly<
  Record<Category, readonly ChecklistItem[]>
> = (() => {
  const map: Record<Category, ChecklistItem[]> = {
    "safety-equipment": [],
    engine: [],
    electronics: [],
    navigation: [],
    boat: [],
    crew: [],
    paperwork: [],
  };
  for (const item of CHECKLIST) {
    map[item.category].push(item);
  }
  return map;
})();

export const CHECKLIST_IDS: readonly string[] = CHECKLIST.map((i) => i.id);
